import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, MessageSquare } from 'lucide-react';
import Tooltip from '../../../components/ui/Tooltip';
import { fmtScore, isDisagreement, weightPercent } from './debriefModel';

// Karşılaştırma tablosu: satır = yetkinlik, sütun = değerlendirici.
// Yatay taşma YALNIZ bu kabın içinde (geniş panel); dikey kaydırma sayfanındır
// — iç içe dikey kaydırma YOK.
const Scroller = styled.div`
  overflow-x: auto;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  background: #fff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  th, td {
    padding: 10px 12px;
    border-bottom: 1px solid #F2F4F7;
    text-align: center;
    white-space: nowrap;
  }
  th:first-child, td:first-child {
    text-align: left;
    white-space: normal;
    min-width: 180px;
  }
  thead th {
    background: #FAFBFC;
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
    vertical-align: top;
  }
  tbody tr:last-child td { border-bottom: 0; }
`;

const DisagreeRow = styled.tr`
  background: #FFF5E1;
  td:first-child { box-shadow: inset 3px 0 0 #E8A317; }
`;

const EvalName = styled.div`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const EvalMeta = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

export const Pill = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $tone }) => ({
    green: '#E8F8F0', red: '#FEE6E6', amber: '#FFF5E1', blue: '#E8F4FD',
  }[$tone] || '#EEF1F4')};
  color: ${({ $tone }) => ({
    green: '#1A6E38', red: '#A02525', amber: '#8A5500', blue: '#024F87',
  }[$tone] || '#3E4A5A')};
`;

const Weight = styled.span`
  margin-left: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const DisagreeTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #8A5500;
`;

const ScoreCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export const REC_TONE = {
  strong_yes: 'green', yes: 'green', neutral: 'neutral', no: 'red', strong_no: 'red',
};

export default function DebriefComparisonTable({ evaluators = [], rows = [] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scoreOf = (row, userId) => (row.scores || []).find((s) => s.userId === userId);

  return (
    <Scroller>
      <Table data-role="debrief-comparison">
        <thead>
          <tr>
            <th>{t('ats.debrief.table.competency')}</th>
            {evaluators.map((ev) => (
              <th key={ev.userId} data-role="debrief-evaluator">
                <EvalName>{ev.name || t('ats.debrief.unknownEvaluator')}</EvalName>
                <EvalMeta>
                  {ev.role ? t(`ats.debrief.role.${ev.role}`, { defaultValue: ev.role }) : null}
                  {ev.submitted
                    ? <CheckCircle2 size={12} color="#1A6E38" aria-label={t('ats.debrief.submitted')} />
                    : <Clock size={12} color="#8A5500" aria-label={t('ats.debrief.notSubmitted')} />}
                </EvalMeta>
                {ev.submitted ? (
                  <>
                    {ev.overallScore != null && (
                      <EvalMeta>{t('ats.debrief.overallScoreShort', { value: fmtScore(ev.overallScore, lang) })}</EvalMeta>
                    )}
                    {ev.recommendation && (
                      <Pill $tone={REC_TONE[ev.recommendation]}>
                        {t(`ats.scorecard.recommendation.${ev.recommendation}`, { defaultValue: ev.recommendation })}
                      </Pill>
                    )}
                  </>
                ) : (
                  <Pill $tone="amber">{t('ats.debrief.notSubmitted')}</Pill>
                )}
              </th>
            ))}
            <th>{t('ats.debrief.table.min')}</th>
            <th>{t('ats.debrief.table.max')}</th>
            <th>{t('ats.debrief.table.spread')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const disagree = isDisagreement(row);
            const RowTag = disagree ? DisagreeRow : 'tr';
            const w = weightPercent(row.weight);
            return (
              <RowTag key={row.key} data-role="debrief-row" data-disagreement={disagree ? 'true' : 'false'}>
                <td>
                  <div>
                    {row.label}
                    {w != null && <Weight>{t('ats.debrief.table.weight', { value: w })}</Weight>}
                  </div>
                  {row.scaleMin != null && row.scaleMax != null && (
                    <Muted style={{ fontSize: 11 }}>
                      {t('ats.debrief.table.scale', { min: row.scaleMin, max: row.scaleMax })}
                    </Muted>
                  )}
                  {disagree && (
                    <div>
                      <DisagreeTag data-role="debrief-disagreement">
                        <AlertTriangle size={12} />
                        {t('ats.debrief.table.disagreement')}
                      </DisagreeTag>
                    </div>
                  )}
                </td>
                {evaluators.map((ev) => {
                  const s = scoreOf(row, ev.userId);
                  const val = fmtScore(s?.score, lang);
                  return (
                    <td key={ev.userId}>
                      {val == null ? <Muted>—</Muted> : (
                        <ScoreCell>
                          {val}
                          {s?.note && (
                            <Tooltip text={s.note}>
                              <MessageSquare size={12} aria-label={t('ats.debrief.table.note')} />
                            </Tooltip>
                          )}
                        </ScoreCell>
                      )}
                    </td>
                  );
                })}
                <td>{fmtScore(row.min, lang) ?? <Muted>—</Muted>}</td>
                <td>{fmtScore(row.max, lang) ?? <Muted>—</Muted>}</td>
                <td data-role="debrief-spread">{fmtScore(row.spread, lang) ?? <Muted>—</Muted>}</td>
              </RowTag>
            );
          })}
        </tbody>
      </Table>
    </Scroller>
  );
}
